<template>
  <d2-container>
    <div class="chat-container">
      <div class="chat-header">
        <div class="chat-title">
          <i class="el-icon-robot"></i>
          <span>栗宝</span>
        </div>
        <div class="chat-user-selector">
          <span  class="selector-label">当前用户：</span>
          <span v-if="selectedUser" class="selected-user">
            <img v-if="selectedUser.avatar" :src="selectedUser.avatar" class="user-avatar" />
            <span class="user-info">{{ selectedUser.nickname || selectedUser.id }}</span>
          </span>
          <el-button size="mini" type="text" @click="showUserSelector = true" class="user-select-btn">
            <i class="el-icon-user-solid"></i> {{ selectedUser ? '更换' : '选择' }}
          </el-button>
          <!-- 用户选择器组件 -->
          <users-select
            :visible.sync="showUserSelector"
            :selectList.sync="userSelectList"
            :limit="1"
            @on-select="onUserSelected"
          ></users-select>
        </div>
      </div>

      <div class="chat-main-content">
        <!-- 动画播放区域 -->
        <div class="animation-container">
          <div class="robot-frame">
            <div class="robot-head">
                              <div class="robot-antenna"></div>
                <div class="robot-face">
                  <canvas ref="animationCanvas" width="260" height="200" class="animation-canvas" v-if="currentAnimation" style="border:1px solid #ccc;"></canvas>
                  <div class="robot-eyes" v-else>
                    <div class="robot-eye"></div>
                    <div class="robot-eye"></div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        <div class="chat-messages" ref="chatMessages">
        <!-- 欢迎语 -->
        <div class="chat-welcome" v-if="messages.length === 0">
          <div class="welcome-avatar">
            <i class="el-icon-robot"></i>
          </div>
          <h3>欢迎与栗宝对话</h3>
          <p>您可以询问我任何问题，我会尽力帮助您。</p>
          <div class="quick-questions">
            <el-button size="small" @click="useQuickQuestion('介绍一下你自己')">介绍一下你自己</el-button>
            <el-button size="small" @click="useQuickQuestion('你能做什么')">你能做什么</el-button>
            <el-button size="small" @click="useQuickQuestion('查询我的任务状态')">我的任务状态</el-button>
            <el-button size="small" @click="useQuickQuestion('帮我写一篇小红书')">帮我写一篇小红书</el-button>
          </div>
        </div>

        <!-- 聊天消息 -->
        <div v-for="(msg, idx) in messages" :key="idx" :class="['chat-message-wrapper', msg.type]">
          <div class="avatar" v-if="msg.type !== 'user'">
            <i class="el-icon-robot"></i>
          </div>
          <div class="message-content">
            <div class="chat-message">
              <div class="chat-meta" v-if="msg.emotion || msg.flow">
                <span v-if="msg.emotion" class="chat-emotion">
                  <i class="el-icon-magic-stick"></i> {{ msg.emotion }}
                </span>
                <span v-if="msg.flow" class="chat-flow" @click="loadAndPlayAnimation(msg.flow)">
                  <i class="el-icon-video-play"></i>
                  <span class="flow-text">播放动画</span>
                </span>
              </div>
              <div class="chat-text" v-html="msg.text.replace(/\n/g, '<br>')"></div>
            </div>
          </div>
          <div class="avatar user-avatar-container" v-if="msg.type === 'user'">
            <img v-if="selectedUser && selectedUser.avatar" :src="selectedUser.avatar" class="user-chat-avatar" />
            <i v-else class="el-icon-user"></i>
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="chat-message-wrapper assistant">
          <div class="avatar">
            <i class="el-icon-robot"></i>
          </div>
          <div class="message-content">
            <div class="chat-message">
              <div class="chat-text loading">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div><!-- 关闭 chat-main-content -->

      <div class="chat-input-wrapper">
        <div class="chat-input">
          <el-input
            v-model="userInput"
            placeholder="请输入您的问题..."
            type="textarea"
            :rows="1"
            :autosize="{ minRows: 1, maxRows: 4 }"
            @keyup.enter.native="onEnterPress"
            resize="none"
          />
          <el-button
            type="primary"
            icon="el-icon-s-promotion"
            :loading="loading"
            @click="sendMessage"
            :disabled="!userInput.trim()"
            class="send-button"
          >发送</el-button>
        </div>
        <div class="chat-footer">
          <span class="footer-tip">按 Enter 发送，Shift + Enter 换行</span>
        </div>
      </div>
    </div>
  </d2-container>
</template>

<script>
import { sendLeeBotStream } from '@/api/merchant/leeBot'
import AnimationPlayer from '@/utils/animation_player'
import { getAnimFlowDetail } from '@/api/merchant/botAnimFlow'
import UsersSelect from '@/components/users/index'

export default {
  name: 'LeeBotChat',
  components: {
    UsersSelect
  },
  data () {
    return {
      userInput: '',
      messages: [],
      loading: false,
      animationPlayer: null,
      currentAnimation: null,
      isPlaying: false,
      _animTimeout: null, // 存储动画超时计时器ID
      showUserSelector: false, // 是否显示用户选择器
      userSelectList: [], // 用户选择器选中的用户列表
      selectedUser: null // 当前选中的用户对象
    }
  },
  watch: {
    selectedUser (newVal, oldVal) {
      if (newVal !== oldVal && oldVal !== null) {
        // 用户变更时，询问是否清空聊天记录
        this.$confirm('切换用户将清空当前聊天记录，是否继续？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          // 清空聊天记录
          this.messages = []
        }).catch(() => {
          // 取消切换，恢复原用户
          this.selectedUser = oldVal
          this.userSelectList = oldVal ? [oldVal] : []
        })
      } else if (newVal !== null && oldVal === null) {
        // 首次选择用户，添加欢迎信息
        if (this.messages.length === 0) {
        //   this.messages.push({
        //     type: 'system',
        //     text: `已选择用户: ${newVal.nickname || newVal.id}，您可以开始对话了`
        //   });
        }
      }
    },
    currentAnimation (newVal, oldVal) {
      console.log('==== 动画状态变更 ====')
      console.log('oldVal:', oldVal)
      console.log('newVal:', newVal)
      console.log('canvas元素:', this.$refs.animationCanvas)
      console.log('animationPlayer:', this.animationPlayer)

      if (newVal) {
        // 每次变更都确保Canvas是可见的
        this.$nextTick(() => {
          const canvas = this.$refs.animationCanvas
          if (canvas) {
            console.log('Canvas就绪状态:', {
              width: canvas.width,
              height: canvas.height,
              display: canvas.style.display,
              visibility: canvas.style.visibility
            })
          }
        })
      }
    }
  },
  methods: {
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.chatMessages
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    onEnterPress (e) {
      // 如果是 shift+enter 组合，则允许换行
      if (e.shiftKey) return
      // 否则阻止默认换行并发送消息
      e.preventDefault()
      this.sendMessage()
    },
    useQuickQuestion (question) {
      this.userInput = question
      this.sendMessage()
    },
    sendMessage () {
      const input = this.userInput.trim()
      if (!input || this.loading) return

      // 验证是否已选择用户
      if (!this.selectedUser) {
        this.$message.warning('请先选择一个用户')
        return
      }

      this.loading = true
      this.messages.push({
        text: input,
        type: 'user',
        emotion: '',
        flow: ''
      })
      this.scrollToBottom()

      sendLeeBotStream({
        userInput: input,
        userId: this.selectedUser ? this.selectedUser.id : '', // 传递用户ID
        onMessage: (msg) => {
          if (msg.text) {
            this.messages.push(msg)
          }
          this.scrollToBottom()

          // 如果消息中包含动画场景ID，自动加载并播放
          if (msg.flow && msg.type === 'assistant') {
            this.loadAndPlayAnimation(msg.flow)
          }
        },
        onError: (err) => {
          this.$message.error('发送失败: ' + (err && err.toString ? err.toString() : err))
        },
        onComplete: () => {
          this.loading = false
        }
      })
      this.userInput = ''
    },

    // 初始化动画播放器
    initAnimationPlayer () {
      if (!this.animationPlayer && this.$refs.animationCanvas) {
        // 使用与Canvas实际大小一致的尺寸初始化
        const canvas = this.$refs.animationCanvas
        this.animationPlayer = new AnimationPlayer({
          canvas: canvas,
          width: canvas.width,
          height: canvas.height
        })

        // 监听播放结束事件
        this.animationPlayer.onEnded(() => {
          this.isPlaying = false
        })
      }
    },

    // 加载并播放动画工作流
    loadAndPlayAnimation (flowId) {
      if (!flowId) return

      // 先销毁旧的播放器以防止白屏
      if (this.animationPlayer) {
        this.animationPlayer.destroy()
        this.animationPlayer = null
      }

      this.currentAnimation = flowId

      this.$nextTick(() => {
        // 检查Canvas元素
        if (this.$refs.animationCanvas) {
          console.log('Canvas准备就绪:', this.$refs.animationCanvas.width, 'x', this.$refs.animationCanvas.height)
        } else {
          console.error('Canvas元素未找到!')
          return // 如果Canvas不存在，直接返回
        }

        this.initAnimationPlayer()

        if (this.animationPlayer) {
          // 使用 getAnimFlowDetail 获取工作流数据
          getAnimFlowDetail(flowId)
            .then(res => {
              if (!res || !res.json) {
                throw new Error('工作流数据格式错误')
              }

              // 解析工作流JSON
              let flowData
              try {
                flowData = typeof res.json === 'string' ? JSON.parse(res.json) : res.json
                console.log('成功解析工作流数据:', flowData)
                // 检查工作流中是否包含nodes和edges
                if (!flowData.nodes || !flowData.edges) {
                  console.warn('工作流数据结构异常，缺少nodes或edges:', flowData)
                }

                // 设置超时，确保动画最终会结束（防止卡住）
                const animTimeout = setTimeout(() => {
                  if (this.currentAnimation) {
                    console.log('动画播放超时，强制结束')
                    this.currentAnimation = null
                    this.isPlaying = false
                  }
                }, 15000) // 15秒后强制结束

                // 存储超时ID，以便可以在正常结束时清除
                this._animTimeout = animTimeout
              } catch (e) {
                console.error('解析工作流JSON失败:', e)
                throw new Error('解析工作流失败')
              }

              // 使用 loadFromFlow 加载工作流
              this.animationPlayer.loadFromFlow(flowData, {
                onStatusChange: (status) => {
                  console.log('工作流状态变化:', status)
                  if (status === 'playing') {
                    this.isPlaying = true
                  } else if (status === 'stopped' || status === 'ended') {
                    this.isPlaying = false
                    // 当工作流结束时，将画面恢复为默认的眼睛动画
                    if (status === 'ended') {
                      // 清除超时计时器
                      if (this._animTimeout) {
                        clearTimeout(this._animTimeout)
                        this._animTimeout = null
                      }
                      // 恢复默认动画
                      this.currentAnimation = null
                      console.log('动画已结束，恢复默认显示')
                    }
                  }
                },
                onNodeChange: (node) => {
                  console.log('当前节点:', node)
                }
              })

              // 播放工作流
              this.playAnimation()
              this.scrollToBottom() // 确保动画区域可见
            })
            .catch(err => {
              console.error('加载工作流失败:', err)
              this.$message.error('加载动画失败: ' + (err.message || err))
              this.currentAnimation = null
            })
        }
      })
    },

    // 播放动画
    playAnimation () {
      if (this.animationPlayer) {
        // 检查画布状态
        console.log('播放前画布状态:', {
          canvas: this.$refs.animationCanvas,
          width: this.$refs.animationCanvas.width,
          height: this.$refs.animationCanvas.height,
          visible: !!this.currentAnimation
        })

        // 确保画布可见并清晰显示
        if (this.$refs.animationCanvas) {
          this.$refs.animationCanvas.style.border = '1px solid #ccc'
          this.$refs.animationCanvas.style.display = 'block'

          // 强制重绘canvas元素
          const context = this.$refs.animationCanvas.getContext('2d')
          context.clearRect(0, 0, this.$refs.animationCanvas.width, this.$refs.animationCanvas.height)
        }

        // 直接播放，不使用延迟
        this.animationPlayer.playFlow()
        this.isPlaying = true
      }
    },

    // 暂停动画
    pauseAnimation () {
      if (this.animationPlayer) {
        // 工作流没有暂停方法，只能停止
        this.stopAnimation()
      }
    },

    // 停止动画
    stopAnimation () {
      if (this.animationPlayer) {
        // 使用 stopFlow 停止工作流
        this.animationPlayer.stopFlow()
        this.isPlaying = false
      }
    },

    // 关闭动画
    closeAnimation () {
      if (this.animationPlayer) {
        this.animationPlayer.stop()
      }
      this.currentAnimation = null
      this.isPlaying = false
    },

    // 处理用户选择
    onUserSelected (users) {
      if (users && users.length > 0) {
        this.selectedUser = users[0]
      } else {
        this.selectedUser = null
      }
    }
  },

  // 销毁动画播放器
  beforeDestroy () {
    // 清除超时计时器
    if (this._animTimeout) {
      clearTimeout(this._animTimeout)
      this._animTimeout = null
    }

    // 销毁动画播放器
    if (this.animationPlayer) {
      try {
        console.log('组件销毁，清理动画播放器')
        this.animationPlayer.stop()
        this.animationPlayer.destroy()
        this.animationPlayer = null
      } catch (e) {
        console.error('销毁动画播放器时出错:', e)
      }
    }

    // 清除其他状态
    this.currentAnimation = null
    this.isPlaying = false
  }
}
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  max-width: 1200px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chat-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: linear-gradient(135deg, #4c84ff 0%, #2d5bff 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
}

.chat-title i {
  font-size: 22px;
  margin-right: 10px;
}

.chat-user-selector {
  display: inline-flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 18px;
  padding: 3px 8px;
}

.selector-label {
  color: rgba(255, 255, 255, 0.9);
  margin-right: 4px;
  font-size: 13px;
}

.chat-user-selector >>> .el-select {
  width: 180px;
}

.chat-user-selector >>> .el-input__inner {
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
}

.chat-user-selector >>> .el-select .el-input .el-select__caret {
  color: white;
}

.user-select-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  padding: 3px 8px !important;
  font-size: 12px !important;
}

.user-select-btn:hover {
  color: #fff !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px;
}

.selected-user {
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 15px;
  padding: 2px 10px 2px 6px;
  max-width: 150px;
}

.user-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 6px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.user-info {
  color: white;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 动画播放区域 */
.animation-container {
  position: relative;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f3f9;
  border-right: 1px solid #eee;
  width: 320px;
  flex-shrink: 0;
}

.robot-frame {
  position: relative;
}

.robot-head {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.robot-antenna {
  width: 10px;
  height: 20px;
  background: #1d56c5;
  position: relative;
  margin-bottom: -5px;
  border-radius: 5px;
  z-index: 1;
}

.robot-antenna:before,
.robot-antenna:after {
  content: '';
  position: absolute;
  bottom: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4c84ff;
}

.robot-antenna:before {
  left: -10px;
}

.robot-antenna:after {
  right: -10px;
}

.robot-face {
  width: 280px;
  height: 220px;
  background: linear-gradient(to bottom, #4c84ff, #2d5bff);
  border-radius: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  padding: 10px;
}

.animation-canvas {
  border-radius: 10px;
  background-color: white;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
  display: block; /* 确保Canvas是块级元素 */
  margin: 0 auto; /* 居中显示 */
}

.robot-eyes {
  display: flex;
  justify-content: space-around;
  width: 160px;
}

.robot-eye {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
}

.robot-eye:after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: #333;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: blink 3s infinite;
}

@keyframes blink {
  0%, 95%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  97% {
    transform: translate(-50%, -50%) scale(0.1, 0);
  }
}

/* 移除控制按钮相关样式 */
.animation-controls {
  display: none;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #f9fafc;
  width: calc(100% - 320px);
}

.chat-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  height: 100%;
}

.welcome-avatar {
  width: 80px;
  height: 80px;
  background-color: #4c84ff;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 42px;
  margin-bottom: 20px;
  box-shadow: 0 8px 16px rgba(76, 132, 255, 0.3);
}

.chat-welcome h3 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.chat-welcome p {
  color: #666;
  margin-bottom: 20px;
  font-size: 16px;
}

.quick-questions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
}

.quick-questions .el-button {
  border-radius: 20px;
}

.chat-message-wrapper {
  display: flex;
  margin-bottom: 20px;
}

.chat-message-wrapper.user {
  flex-direction: row-reverse;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.chat-message-wrapper.assistant .avatar,
.chat-message-wrapper.system .avatar {
  background-color: #4c84ff;
  color: white;
}

.chat-message-wrapper.user .avatar {
  background-color: #42b983;
  color: white;
}

.message-content {
  max-width: 80%;
  margin: 0 12px;
}

.chat-message {
  border-radius: 18px;
  padding: 12px 16px;
  position: relative;
  transition: all 0.3s;
}

.chat-message-wrapper.assistant .chat-message {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-bottom-left-radius: 4px;
}

.chat-message-wrapper.user .chat-message {
  background-color: #ecf5ff;
  color: #333;
  border-bottom-right-radius: 4px;
}

.chat-message-wrapper.system .chat-message {
  background-color: #fffbe6;
  color: #333;
  text-align: center;
}

.chat-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12px;
}

.chat-emotion, .chat-flow {
  display: inline-flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  padding: 2px 8px;
}

.chat-flow {
  cursor: pointer;
  transition: all 0.2s;
  background-color: rgba(76, 132, 255, 0.1);
  color: #4c84ff;
}

.chat-flow:hover {
  background-color: rgba(76, 132, 255, 0.2);
}

.chat-flow .flow-text {
  text-decoration: underline;
  margin-left: 4px;
}

.chat-emotion i, .chat-flow i {
  margin-right: 4px;
  font-size: 12px;
}

.chat-text {
  font-size: 15px;
  line-height: 1.5;
  word-break: break-word;
}

.chat-text.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  min-height: 18px;
}

.dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: #4c84ff;
  display: inline-block;
  margin: 0 3px;
  animation: bounce 1.5s infinite ease-in-out;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-5px);
  }
}

.chat-input-wrapper {
  padding: 15px 20px;
  background-color: white;
  border-top: 1px solid #eee;
}

.chat-input {
  display: flex;
  gap: 10px;
}

.chat-input .el-textarea {
  border-radius: 8px;
}

.chat-input .el-textarea >>> .el-textarea__inner {
  resize: none;
  padding: 12px 15px;
  min-height: 24px;
  border-radius: 8px;
  transition: all 0.3s;
  border-color: #dcdfe6;
}

.chat-input .el-textarea >>> .el-textarea__inner:focus {
  border-color: #4c84ff;
  box-shadow: 0 0 0 2px rgba(76, 132, 255, 0.2);
}

.send-button {
  padding: 0 20px;
  border-radius: 8px;
  transition: all 0.3s;
}

.send-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 132, 255, 0.3);
}

.chat-footer {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.footer-tip {
  font-size: 12px;
  color: #999;
}

/* 过渡动画 */
.chat-message-wrapper {
  animation: fade-in 0.3s ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 主内容区域 - 水平排列动画和聊天 */
.chat-main-content {
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .chat-main-content {
    flex-direction: column;
  }

  .animation-container {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #eee;
  }

  .chat-messages {
    width: 100%;
  }

  .chat-container {
    max-width: 100%;
  }
}

.user-avatar-container {
  padding: 0;
  overflow: hidden;
}

.user-chat-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
</style>
